import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationQuestion,
  ApplicationSection,
  CellEdCommonQuestionKeys,
  CommonQuestionKeys,
  FieldType,
  IntroPage,
  StatusOverride,
  TextInputType,
} from '@bybeam/platform-types';
import { inList } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';

export const COMMON_PAGES: { [key: string]: IntroPage } = {
  welcome: {
    key: 'what-is-it',
    layout: 'welcome',
    title: 'What is CalGrows?',
    body: `
CalGrows gives caregivers of older adults and people with disabilities access to free training 
courses, career coaching and up to $6000 in incentives to help increase worker satisfaction 
and retention. CalGrows is a program of the California Department of Aging for in-home and 
community based caregivers.

Through this portal, qualified applicants can apply for 
CalGrows Learn & Earn and Career Builder incentive payments. Tap below to continue your 
application for the {PROGRAM_NAME}.`,
  },
  whatToExpect: {
    key: 'how-do-i-get-it',
    layout: 'default',
    title: 'What to expect',
    body: 'We’ve set up the application process to make it as easy as possible for you. Whatever you’ve entered will be saved for later. You can complete just the personal information section for now and come back to the other sections when you are ready.\n\nAs you prepare to complete your application, we suggest you gather the information you will need when you apply, including proof of employment, training records, and your social security number or EIN.',
  },
  applicationProcess: {
    key: 'application-process',
    layout: 'steps',
    title: 'About the application process. Here are some things to know before you start.',
    steps: [
      {
        title: 'It takes up to 20 minutes to complete',
        icon: 'time',
        description:
          'You can expect to spend 20 minutes or less to complete your application if you have all your materials.',
      },
      {
        title: 'Your progress will be saved as you go',
        icon: 'checkCircleOutline',
        description:
          'You can start your application and finish it later. If you need to come back to complete your application, you can start where you left off.',
      },
      {
        title: 'Enter necessary information to process your request and get paid',
        icon: 'infoOutline',
        description: 'We recommend completing the application in a safe and private space.',
      },
      {
        title: 'Your data is secure',
        icon: 'gppGoodOutlined',
        description:
          'We will safely store your data to help you apply to future applications faster, and we will not share your data with third parties without your consent.',
      },
      {
        title: 'Check your inbox',
        icon: 'mail',
        description: 'We will review your application and notify you once we are done.',
      },
    ],
  },
};

export const COMMON_APPLICATION_PORTAL_STATUSES: Partial<StatusOverride>[] = [
  {
    status: 'InProgress',
    message:
      'You’ve made great progress so far! Keep working on your application to see if you’re eligible for an incentive payment.',
  },
  {
    status: 'Application',
    label: 'Apply for Incentive Track: {PROGRAM_NAME}',
    message:
      'Not sure which is the correct program? Need help? Call us at (855) 916-4091 or email at [help@calgrows.org](mailto:help@calgrows.org) to contact us.',
  },
  {
    status: 'AwaitingReview',
    message:
      'Thank you for submitting your application! We will contact you if we need any additional information. Check back here for updates. The entire process, including payment, may take up to 60 business days.',
  },
  {
    status: 'InReview',
    message:
      'Your application has been viewed and is in the final stage of review before sending your incentive payment. You will receive an email once a final decision has been made and your check is issued.',
  },
];

type ExtractedCommonQuestionKeys = Extract<
  CommonQuestionKeys,
  | CommonQuestionKeys.MAILING_ADDRESS
  | CommonQuestionKeys.EMPLOYER_ADDRESS
  | CommonQuestionKeys.JOB_TITLE
  | CommonQuestionKeys.GENDER
  | CommonQuestionKeys.ETHNICITY
  | CommonQuestionKeys.AGE
  | CommonQuestionKeys.LANGUAGE
  | CommonQuestionKeys.HOUSEHOLD_INCOME
>;

export const COMMON_QUESTIONS: {
  [key in ExtractedCommonQuestionKeys | CellEdCommonQuestionKeys]: ApplicationQuestion;
} = {
  [CommonQuestionKeys.EMPLOYER_ADDRESS]: {
    key: CommonQuestionKeys.EMPLOYER_ADDRESS,
    copy: { title: 'What is your employer’s address?' },
    fields: [
      {
        type: FieldType.Address,
        copy: "Employer's Street Address",
        key: CommonQuestionKeys.EMPLOYER_ADDRESS,
        filter: { state: 'CA' },
      },
    ],
  },
  [CommonQuestionKeys.MAILING_ADDRESS]: {
    key: CommonQuestionKeys.MAILING_ADDRESS,
    copy: {
      title: 'What is your mailing address?',
      intro: 'Your incentive check will be mailed to this address if you’re approved for payment.',
    },
    fields: [
      {
        type: FieldType.Address,
        key: CommonQuestionKeys.MAILING_ADDRESS,
        filter: { state: 'CA' },
      },
    ],
  },
  [CellEdCommonQuestionKeys.EMPLOYMENT_CERTIFICATE]: {
    key: CellEdCommonQuestionKeys.EMPLOYMENT_CERTIFICATE,
    copy: {
      title: 'Please upload your Employment Certificate',
      intro: 'Please upload your Employment Certificate',
    },
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.employmentCertificate',
      },
    ],
  },
  [CellEdCommonQuestionKeys.PAY_STUB]: {
    key: CellEdCommonQuestionKeys.PAY_STUB,
    copy: {
      title: 'Please upload a document with your paystub',
      intro: 'Please upload a document with your paystub',
    },
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.paystub',
      },
    ],
  },
  [CellEdCommonQuestionKeys.PROOF_EMPLOYMENT]: {
    key: CellEdCommonQuestionKeys.PROOF_EMPLOYMENT,
    copy: {
      title: 'Please upload documents showing your proof of employment for at least 60 days.',
      intro: `It can be a paystub, timecard or letter from your supervisor. 

**Proof of 60 days employment using paystubs requires a minimum of TWO paystubs- one at least 60 days prior to taking first course, and a second (most recent) paystub.**

You can upload more than one document/image by tapping the upload button again.`,
    },
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.proofEmployment',
      },
    ],
  },
  [CellEdCommonQuestionKeys.SUPERVISOR_PHONE]: {
    key: CellEdCommonQuestionKeys.SUPERVISOR_PHONE,
    copy: { title: 'What is your supervisor’s phone number?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Supervisor’s phone number',
        key: CellEdCommonQuestionKeys.SUPERVISOR_PHONE,
        inputType: TextInputType.Phone,
      },
    ],
  },
  [CellEdCommonQuestionKeys.SUPERVISOR_NAME]: {
    key: CellEdCommonQuestionKeys.SUPERVISOR_NAME,
    copy: { title: 'What is your supervisor’s first and last name?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Supervisor First Name',
        key: 'supervisorName.firstName',
        inputType: TextInputType.Text,
      },
      {
        type: FieldType.Text,
        copy: 'Supervisor Last Name',
        key: 'supervisorName.lastName',
        inputType: TextInputType.Text,
      },
    ],
  },
  [CommonQuestionKeys.JOB_TITLE]: {
    key: CommonQuestionKeys.JOB_TITLE,
    copy: { title: 'What is your job title?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Job Title',
        key: CommonQuestionKeys.JOB_TITLE,
        inputType: TextInputType.Text,
      },
    ],
  },
  [CommonQuestionKeys.AGE]: {
    key: CommonQuestionKeys.AGE,
    copy: { title: 'How old are you?' },
    skippable: true,
    fields: [
      {
        type: FieldType.RadioList,
        key: 'age.age',
        options: [
          { label: '17 or younger', value: '17-or-younger' },
          { label: '18 - 24', value: '18-24' },
          { label: '25 - 34', value: '25-34' },
          { label: '35 - 44', value: '35-44' },
          { label: '45 - 54', value: '45-54' },
          { label: '55 - 64', value: '55-64' },
          { label: '65+', value: '65+' },
        ],
      },
    ],
  },
  [CommonQuestionKeys.GENDER]: {
    key: CommonQuestionKeys.GENDER,
    copy: { title: 'What is your gender identity?' },
    skippable: true,
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'gender.gender',
        options: [
          { value: 'male', label: 'Male' },
          { value: 'female', label: 'Female' },
          { value: 'transgender-male', label: 'Transgender Male' },
          { value: 'transgender-female', label: 'Transgender Female' },
          {
            value: 'genderqueer/gender-non-conforming/gender-non-binary',
            label: 'Genderqueer/Gender non-conforming/Gender non-binary',
          },
          { value: 'questioning-or-unsure', label: 'Questioning or unsure' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'gender.otherGender',
        dynamicLogic: inList('gender.gender', 'other'),
        copy: 'Other',
      },
    ],
  },
  [CommonQuestionKeys.ETHNICITY]: {
    key: CommonQuestionKeys.ETHNICITY,
    copy: { title: 'What is your racial / ethnic background?' },
    skippable: true,
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'ethnicity.ethnicity',
        options: [
          { value: 'caucasian-white-european', label: 'Caucasian/White/European' },
          { value: 'african-american-black-african', label: 'African-American/Black/African' },
          {
            value: 'american-indian-native-american-alaskan-native',
            label: 'American Indian/Native American/Alaskan Native',
          },
          { value: 'hispanic-latinx', label: 'Hispanic/Latinx' },
          { value: 'asian-pacific-islander', label: 'Asian/Pacific Islander' },
          { value: 'other', label: 'Other' },
        ],
      },
      {
        type: FieldType.Text,
        inputType: TextInputType.Text,
        key: 'ethnicity.otherEthnicity',
        dynamicLogic: inList('ethnicity.ethnicity', 'other'),
        copy: 'Other',
      },
    ],
  },
  [CommonQuestionKeys.LANGUAGE]: {
    key: CommonQuestionKeys.LANGUAGE,
    copy: { title: 'Which language(s) do you speak?' },
    skippable: true,
    fields: [
      {
        type: FieldType.CheckboxGroup,
        key: 'language.language',
        options: [
          { value: 'arabic', label: 'Arabic' },
          { value: 'armenian', label: 'Armenian' },
          { value: 'cambodian', label: 'Cambodian' },
          { value: 'cantonese', label: 'Cantonese' },
          { value: 'english', label: 'English' },
          { value: 'farsi', label: 'Farsi' },
          { value: 'hindi', label: 'Hindi' },
          { value: 'hmong', label: 'Hmong' },
          { value: 'japanese', label: 'Japanese' },
          { value: 'korean', label: 'Korean' },
          { value: 'laotian', label: 'Laotian' },
          { value: 'mandarin', label: 'Mandarin' },
          { value: 'punjabi', label: 'Punjabi' },
          { value: 'russian', label: 'Russian' },
          { value: 'spanish', label: 'Spanish' },
          { value: 'tagalog', label: 'Tagalog' },
          { value: 'thai', label: 'Thai' },
          { value: 'ukrainian', label: 'Ukrainian' },
          { value: 'vietnamese', label: 'Vietnamese' },
          { value: 'other', label: 'Other' },
        ],
      },
    ],
  },
  [CellEdCommonQuestionKeys.EDUCATION_LEVEL]: {
    key: CellEdCommonQuestionKeys.EDUCATION_LEVEL,
    copy: { title: 'What is the highest degree or level of school you have completed?' },
    skippable: true,
    fields: [
      {
        type: FieldType.RadioList,
        key: 'educationLevel.educationLevel',
        options: [
          { value: 'no-schooling-completed', label: 'No schooling completed' },
          { value: 'did-not-finish-high-school', label: 'Did not finish high school' },
          { value: 'high-school-or-ged', label: 'High School or GED' },
          {
            value: 'some-college-or-vocational-school-no-degree',
            label: 'Some College or Vocational School, No Degree',
          },
          { value: 'associates-degree', label: 'Associate’s Degree' },
          { value: 'bachelors-degree', label: "Bachelor's Degree" },
          { value: 'graduate-degree', label: 'Graduate Degree' },
        ],
      },
    ],
  },
  [CommonQuestionKeys.HOUSEHOLD_INCOME]: {
    key: CommonQuestionKeys.HOUSEHOLD_INCOME,
    copy: { title: 'What is your annual household income?' },
    skippable: true,
    fields: [
      {
        type: FieldType.RadioList,
        key: 'householdIncome.annualAmount',
        options: [
          { value: '0-20k', label: '0-20k' },
          { value: '21k-40k', label: '21k-40k' },
          { value: '41k-60k', label: '41k-60k' },
          { value: '61k-80k', label: '61k-80k' },
          { value: '81k-100k', label: '81k-100k' },
          { value: '100k+', label: '100k+' },
        ],
      },
    ],
  },
  [CellEdCommonQuestionKeys.EMPLOYER]: {
    key: CellEdCommonQuestionKeys.EMPLOYER,
    copy: { title: 'Who is your employer?' },
    fields: [
      {
        type: FieldType.Text,
        copy: 'Employer',
        key: CellEdCommonQuestionKeys.EMPLOYER,
        inputType: TextInputType.Text,
      },
    ],
  },
};

export const COMMON_SECTIONS: { [key: string]: ApplicationSection } = {
  personalInfo: {
    key: 'personalInfo',
    name: 'Personal Information',
    overview: {
      title: 'Personal Information',
      description:
        'In this section we’ll gather information about you and your current employment status.',
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'Personal Information',
        questions: [
          COMMON_QUESTIONS[CommonQuestionKeys.MAILING_ADDRESS],
          COMMON_QUESTIONS.jobTitle,
          COMMON_QUESTIONS.employer,
          COMMON_QUESTIONS.employerAddress,
          COMMON_QUESTIONS.supervisorName,
          COMMON_QUESTIONS.supervisorPhone,
          COMMON_QUESTIONS.proofEmployment,
        ],
      },
    ],
  },
  aboutYou: {
    key: 'aboutYou',
    name: 'About You',
    overview: {
      description: `
In this section we will gather some information about you. The questions in this section are not required. 
You can skip any questions that you prefer not to answer.`,
      title: 'About You',
    },
    questionGroups: [
      {
        key: 'aboutYou',
        name: 'About You',
        questions: [
          COMMON_QUESTIONS[CommonQuestionKeys.AGE],
          COMMON_QUESTIONS.gender,
          COMMON_QUESTIONS.ethnicity,
          COMMON_QUESTIONS[CommonQuestionKeys.LANGUAGE],
          COMMON_QUESTIONS.educationLevel,
          COMMON_QUESTIONS[CommonQuestionKeys.HOUSEHOLD_INCOME],
        ],
      },
    ],
  },
  review: mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections.review,
      {
        overview: {
          description: `
Next you will review your application. Please make sure all the information you provided
is correct, so we can review your application as soon as possible.`,
        },
      } as ApplicationSection,
    ],
    'merge',
  ),
};
